import type { TodoNoteItemDraft } from '../types/todoNote';
import { toISODate } from './date';

function parseMonthYear(iso: string): { y: number; m: number } | null {
  const [ys, ms] = iso.split('-');
  const y = Number(ys);
  const m = Number(ms);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return { y, m };
}

/** 월 단위 마감 블럭의 해당 월 1일 (타임테이블 정렬·due_date와 동일 기준) */
function todoItemMonthFirstDayIso(iso: string): string | null {
  const parsed = parseMonthYear(iso);
  if (!parsed) return null;
  const { y, m } = parsed;
  return toISODate(new Date(y, m - 1, 1));
}

/**
 * 스크롤·범위 판별용 마감일.
 * - 일 단위: due_date
 * - 월 단위: 해당 월 1일 (리스트에서 월 블럭이 그 달 맨 위에 오는 것과 동일)
 */
export function todoItemEffectiveDueIso(
  item: Pick<TodoNoteItemDraft, 'due_kind' | 'due_date'>
): string | null {
  const iso = String(item.due_date ?? '').trim();
  if (!iso) return null;

  if (item.due_kind === 'month') {
    return todoItemMonthFirstDayIso(iso);
  }

  return iso.slice(0, 10);
}

/**
 * 타임테이블 자동 스크롤 대상:
 * 정렬된 목록(과거→미래)에서 미완료 블럭 중 가장 위(가장 오래된) 항목.
 */
export function findTimetableAutoScrollItemId(items: TodoNoteItemDraft[]): string | null {
  for (const item of items) {
    if (item.completed) continue;
    return item.id;
  }
  return null;
}

function canScrollVertically(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  if (oy !== 'auto' && oy !== 'scroll' && style.overflow !== 'auto' && style.overflow !== 'scroll') {
    return false;
  }
  return el.scrollHeight > el.clientHeight + 1;
}

/** preferred → 조상 중 실제로 세로 스크롤 가능한 첫 요소 */
function resolveTodoNoteVerticalScrollTarget(preferred: HTMLElement): HTMLElement | null {
  if (canScrollVertically(preferred)) return preferred;
  let p: HTMLElement | null = preferred.parentElement;
  while (p) {
    if (canScrollVertically(p)) return p;
    p = p.parentElement;
  }
  const root = document.scrollingElement;
  if (root instanceof HTMLElement && root.scrollHeight > root.clientHeight + 1) {
    return root;
  }
  return null;
}

function stickyHeadOffset(scrollContainer: HTMLElement): number {
  const columnHead = scrollContainer.querySelector('.cp-todo-note-column-head');
  const categoryMemo = scrollContainer.querySelector('.cp-todo-note-category-memo-strip');
  return (
    (columnHead instanceof HTMLElement && columnHead.offsetParent !== null
      ? columnHead.offsetHeight
      : 0) +
    (categoryMemo instanceof HTMLElement && categoryMemo.offsetParent !== null
      ? categoryMemo.offsetHeight
      : 0) +
    4
  );
}

/**
 * 대상 행을 스크롤 영역 상단(스티키 헤더 아래)에 맞춘다.
 * 레이아웃이 아직 안 잡혔거나 정렬 실패면 false → 호출측에서 재시도.
 */
export function scrollTodoNoteTimetableToItem(
  scrollContainer: HTMLElement,
  itemId: string
): boolean {
  const row = scrollContainer.querySelector(`[data-todo-item-id="${CSS.escape(itemId)}"]`);
  if (!(row instanceof HTMLElement)) return false;

  const headOffset = stickyHeadOffset(scrollContainer);
  const expectedTop = () => scrollContainer.getBoundingClientRect().top + headOffset;
  const isAligned = () => Math.abs(row.getBoundingClientRect().top - expectedTop()) <= 14;

  const target = resolveTodoNoteVerticalScrollTarget(scrollContainer);
  if (!target) {
    // 스크롤할 여유가 없어도 이미 상단에 보이면 성공
    return isAligned();
  }

  const containerTop = target.getBoundingClientRect().top;
  const rowTop = row.getBoundingClientRect().top;
  const offset = target === scrollContainer ? headOffset : 4;
  target.scrollTop = target.scrollTop + (rowTop - containerTop) - offset;

  return isAligned() || Math.abs(row.getBoundingClientRect().top - (target.getBoundingClientRect().top + offset)) <= 14;
}

/** 찾기 활성 매치를 스크롤 영역 안( sticky 열 머리 아래 )에 보이도록 이동 */
export function scrollTodoNoteSearchMatchIntoView(
  scrollContainer: HTMLElement,
  matchEl: HTMLElement
): boolean {
  const pad = 8;
  const containerRect = scrollContainer.getBoundingClientRect();
  const elRect = matchEl.getBoundingClientRect();

  const inCategoryMemo = matchEl.closest('.cp-todo-note-category-memo-strip') != null;
  const columnHead = scrollContainer.querySelector('.cp-todo-note-column-head');
  const stickyHeadHeight =
    !inCategoryMemo && columnHead instanceof HTMLElement ? columnHead.offsetHeight : 0;

  const visibleTop = containerRect.top + stickyHeadHeight + pad;
  const visibleBottom = containerRect.bottom - pad;

  let deltaY = 0;
  if (elRect.top < visibleTop) {
    deltaY = elRect.top - visibleTop;
  } else if (elRect.bottom > visibleBottom) {
    deltaY = elRect.bottom - visibleBottom;
  }

  const visibleLeft = containerRect.left + pad;
  const visibleRight = containerRect.right - pad;
  let deltaX = 0;
  if (elRect.left < visibleLeft) {
    deltaX = elRect.left - visibleLeft;
  } else if (elRect.right > visibleRight) {
    deltaX = elRect.right - visibleRight;
  }

  if (deltaY === 0 && deltaX === 0) return true;

  const target = resolveTodoNoteVerticalScrollTarget(scrollContainer) ?? scrollContainer;
  target.scrollTop += deltaY;
  if (target === scrollContainer) scrollContainer.scrollLeft += deltaX;
  return true;
}
